/**
 * SeatedShowcase
 *
 * Animated showcase component for tick-etc.com that demonstrates the
 * full seated-events ticketing flow:
 *   1. Browse the map (tier filter + section pick)
 *   2. Pick your seat (seat-row UI + selection)
 *   3. Get your QR (ticket card + save action)
 *   4. Scan & enter (gate scanner + welcome bubble)
 *
 * The animation runs on a scripted timeline driven by a custom hook.
 * A short stadium intro video plays first; when it ends the freeze
 * frame is shown and the UI showcase loops on top of it.
 *
 * Usage:
 *   import { SeatedShowcase } from './SeatedShowcase/SeatedShowcase';
 *   import './SeatedShowcase/SeatedShowcase.css';
 *   ...
 *   <SeatedShowcase />
 *
 * Required public assets (place in /public):
 *   /videos/basis_video_stadion.mp4
 *   /images/stadium-freeze-frame.jpg
 *   /images/stadium-ticket-bg.jpg
 *   /images/ajax-logo.png
 *   /images/feyenoord-logo.png
 */
import React, { useCallback, useEffect, useRef } from 'react';
import { useShowcaseTimeline } from './useShowcaseTimeline';

export function SeatedShowcase() {
  // Refs grouped in an object for easy passing to the hook
  const refs = {
    sectionRef: useRef(null),
    stageRef: useRef(null),
    bgVideoRef: useRef(null),
    freezeImgRef: useRef(null),
    initialMarkerRef: useRef(null),
    stepIndicatorRef: useRef(null),
    sectionTooltipRef: useRef(null),
    sectionDetailRef: useRef(null),
    sdCtaRef: useRef(null),
    btnPrimaryRef: useRef(null),
    btnPrimaryLabelRef: useRef(null),
    btnCloseRef: useRef(null),
    pricebarTitleRef: useRef(null),
    pricebarSubRef: useRef(null),
    cursorRef: useRef(null),
    timerRef: useRef(null),
    ticketCardRef: useRef(null),
    qrCardRef: useRef(null),
    ticketBtnSaveRef: useRef(null),
    ticketBtnWalletRef: useRef(null),
    gateScannerRef: useRef(null),
  };

  const { start, prepareIntro } = useShowcaseTimeline(refs);
  const hasStartedOnceRef = useRef(false);
  const fallbackTimerRef = useRef(null);

  const clearFallback = useCallback(() => {
    if (!fallbackTimerRef.current) return;
    clearTimeout(fallbackTimerRef.current);
    fallbackTimerRef.current = null;
  }, []);

  const restartFromIntro = useCallback(() => {
    const video = refs.bgVideoRef.current;
    if (!video) return;

    clearFallback();
    hasStartedOnceRef.current = true;
    prepareIntro();
    video.play().catch(() => {});
    fallbackTimerRef.current = setTimeout(() => {
      if (video.paused || video.ended) {
        clearFallback();
        start();
      }
    }, 7000);
  }, [clearFallback, prepareIntro, refs.bgVideoRef, start]);

  const handleManualRestart = useCallback(() => {
    restartFromIntro();
  }, [restartFromIntro]);

  useEffect(() => {
    const section = refs.sectionRef.current;
    const video = refs.bgVideoRef.current;
    let observer = null;

    if (!section || !video) return undefined;

    const onEnded = () => {
      clearFallback();
      start();
    };

    video.addEventListener('ended', onEnded);

    observer = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting) return;
      if (hasStartedOnceRef.current) return;
      restartFromIntro();
    }, { threshold: 0.75 });

    observer.observe(section);

    return () => {
      observer?.disconnect();
      if (video) video.removeEventListener('ended', onEnded);
      clearFallback();
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
    };
  }, [clearFallback, restartFromIntro, refs.bgVideoRef, refs.sectionRef, start]);

  return (
    <section className="tick-etc-section" ref={refs.sectionRef}>

  {/* Header text ABOVE the box */}
  <div className="section-header">
    <div className="section-eyebrow">Seated Events</div>
    <h2 className="section-title">
      Pick your seat. <span className="accent">Get your QR. Walk in.</span>
    </h2>
    <p className="section-subtitle">
      From the online ticketshop to the gate {"\u2014"} Tick-etc. handles seated stadium ticketing in tiers,
      with a scannable QR that validates access and your reserved seat at the venue.
    </p>
  </div>

  {/* Premium frame around the showcase box */}
  <div className="showcase-frame">

    {/* Feature list in left empty space */}
      <aside className="frame-features" aria-label="Section highlights">
        <div className="ff-eyebrow">Premium features</div>
        <h3 className="ff-title">Built for the<br />full match-day flow</h3>

      <ul className="ff-list">
        <li className="ff-item">
          <span className="ff-icon">
            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M10 2 L17 6 V11 C17 14.5 14 17 10 18 C6 17 3 14.5 3 11 V6 Z"
                    stroke="currentColor" strokeWidth="1.6"
                    strokeLinejoin="round" fill="none"/>
              <path d="M7 10 L9 12 L13 8" stroke="currentColor" strokeWidth="1.6"
                    strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
          <div className="ff-text">
            <div className="ff-h">Tier-based pricing</div>
            <div className="ff-d">Bronze, Silver, Gold & VIP — clear value at a glance</div>
          </div>
        </li>

        <li className="ff-item">
          <span className="ff-icon">
            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <rect x="3" y="4" width="14" height="12" rx="2"
                    stroke="currentColor" strokeWidth="1.6"/>
              <circle cx="10" cy="10" r="2.4" stroke="currentColor" strokeWidth="1.6"/>
              <path d="M3 8 H17" stroke="currentColor" strokeWidth="1.6"/>
            </svg>
          </span>
          <div className="ff-text">
            <div className="ff-h">Interactive seat map</div>
            <div className="ff-d">Pick exact section, row and seat in seconds</div>
          </div>
        </li>

        <li className="ff-item">
          <span className="ff-icon">
            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <rect x="4" y="4" width="5" height="5" stroke="currentColor" strokeWidth="1.6"/>
              <rect x="11" y="4" width="5" height="5" stroke="currentColor" strokeWidth="1.6"/>
              <rect x="4" y="11" width="5" height="5" stroke="currentColor" strokeWidth="1.6"/>
              <rect x="12" y="12" width="2" height="2" fill="currentColor"/>
              <rect x="15" y="15" width="1.5" height="1.5" fill="currentColor"/>
            </svg>
          </span>
          <div className="ff-text">
            <div className="ff-h">Scannable QR ticket</div>
            <div className="ff-d">Saved to wallet, validated at the gate in milliseconds</div>
          </div>
        </li>

        <li className="ff-item">
          <span className="ff-icon">
            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M3 7 L10 3 L17 7 L17 9 L3 9 Z" stroke="currentColor" strokeWidth="1.6"
                    strokeLinejoin="round"/>
              <path d="M5 9 V16 M15 9 V16 M9 9 V16 M11 9 V16" stroke="currentColor" strokeWidth="1.6"
                    strokeLinecap="round"/>
              <path d="M3 16 H17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
          </span>
          <div className="ff-text">
            <div className="ff-h">Ticket Marketplace</div>
            <div className="ff-d">Season cards, resell your seat and loyalty program</div>
          </div>
        </li>

        <li className="ff-item">
          <span className="ff-icon">
            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M3 10 C3 6 6 3 10 3 C14 3 17 6 17 10"
                    stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
              <path d="M5.5 10 C5.5 7.5 7.5 5.5 10 5.5 C12.5 5.5 14.5 7.5 14.5 10"
                    stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
                    opacity="0.55"/>
              <circle cx="10" cy="13.5" r="1.6" fill="currentColor"/>
            </svg>
          </span>
          <div className="ff-text">
            <div className="ff-h">POS Tap 2 Pay</div>
            <div className="ff-d">Club application, merchandise & hospitality</div>
          </div>
        </li>
      </ul>

      <button
        type="button"
        className="ff-restart-btn"
        onClick={handleManualRestart}
        aria-label="Restart intro video"
      >
        Restart video
      </button>
    </aside>

    <div className="showcase-box">

      <div className="stage" id="stage" ref={refs.stageRef} data-step="freeze">
  
  {/* Background freeze frame / video */}
  <div className="scene-bg">
    <video id="bgVideo" ref={refs.bgVideoRef} muted playsInline preload="auto"
           poster="/images/stadium-freeze-frame.jpg">
      <source src="/videos/basis_video_stadion.mp4" type="video/mp4" />
    </video>
    <img id="freezeImg" ref={refs.freezeImgRef} src="/images/stadium-frame-poster.jpg" alt="Stadium freeze"
         style={{display: 'none'}} />
  </div>
  <div className="vignette"></div>
  
  {/* Frosted glass overlay (comes on after step 0) */}
  <div className="frost-layer"></div>

  {/* Initial seat marker shown briefly on freeze frame */}
  <div className="initial-marker" id="initialMarker" ref={refs.initialMarkerRef}>
    <div className="pulse"></div>
    <div className="dot"></div>
  </div>

  {/* Step indicator (NEW order: Browse -> Pick -> QR -> Scan) */}
  <div className="step-indicator" id="stepIndicator" ref={refs.stepIndicatorRef}>
    <div className="step" data-step="map">
      <span className="num">01</span><span className="label">Browse the map</span>
    </div>
    <div className="step" data-step="seat">
      <span className="num">02</span><span className="label">Pick your seat</span>
    </div>
    <div className="step" data-step="qr">
      <span className="num">03</span><span className="label">Get your QR</span>
    </div>
    <div className="step" data-step="scan">
      <span className="num">04</span><span className="label">Scan & enter</span>
    </div>
  </div>

  {/* ========= MAP SCENE ========= */}
  
  {/* Price filter chips */}
  <div className="price-filters" id="priceFilters">
    <div className="filter-chip" data-tier="1" id="chip1">
      <span className="swatch"></span>
      <span className="name">Bronze</span>
      <span className="price">€25</span>
    </div>
    <div className="filter-chip" data-tier="2" id="chip2">
      <span className="swatch"></span>
      <span className="name">Silver</span>
      <span className="price">€45</span>
    </div>
    <div className="filter-chip" data-tier="3" id="chip3">
      <span className="swatch"></span>
      <span className="name">Gold</span>
      <span className="price">€85</span>
    </div>
    <div className="filter-chip" data-tier="4" id="chip4">
      <span className="swatch"></span>
      <span className="name">VIP</span>
      <span className="price">€150</span>
    </div>
  </div>

  <div className="map-scene">
    <div className="pitch-stage" id="pitchStage">
      <svg className="pitch-svg" id="pitchSvg" viewBox="0 0 1200 800" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="grassGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1d5a2c" />
            <stop offset="50%" stopColor="#2a7a3e" />
            <stop offset="100%" stopColor="#1d5a2c" />
          </linearGradient>
          <linearGradient id="grassStripe" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2a8045" />
            <stop offset="100%" stopColor="#236b38" />
          </linearGradient>
          <linearGradient id="standBase" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1a2b48" />
            <stop offset="100%" stopColor="#0c1a31" />
          </linearGradient>
        </defs>

        <rect x="60" y="80" width="1080" height="640" rx="180"
              fill="url(#standBase)"
              stroke="rgba(255,255,255,0.06)" strokeWidth="2"/>
        <rect x="120" y="140" width="960" height="520" rx="130"
              fill="#0a172e"
              stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
        
        <rect x="290" y="270" width="620" height="260" rx="6"
              fill="url(#grassGrad)"
              stroke="rgba(255,255,255,0.4)" strokeWidth="1.5"/>
        <g opacity="0.4">
          <rect x="290" y="270" width="62" height="260" fill="url(#grassStripe)"/>
          <rect x="414" y="270" width="62" height="260" fill="url(#grassStripe)"/>
          <rect x="538" y="270" width="62" height="260" fill="url(#grassStripe)"/>
          <rect x="662" y="270" width="62" height="260" fill="url(#grassStripe)"/>
          <rect x="786" y="270" width="62" height="260" fill="url(#grassStripe)"/>
        </g>
        <g stroke="rgba(255,255,255,0.55)" strokeWidth="1.5" fill="none">
          <line x1="600" y1="270" x2="600" y2="530"/>
          <circle cx="600" cy="400" r="42"/>
          <circle cx="600" cy="400" r="2.5" fill="rgba(255,255,255,0.6)" stroke="none"/>
          <rect x="290" y="322" width="60" height="156"/>
          <rect x="290" y="362" width="22" height="76"/>
          <rect x="850" y="322" width="60" height="156"/>
          <rect x="888" y="362" width="22" height="76"/>
          <rect x="285" y="386" width="5" height="28"/>
          <rect x="910" y="386" width="5" height="28"/>
        </g>

        {/* North stand */}
        <g id="standNorth">
          <rect className="section-block" data-id="N1" data-tier="2"
                x="160" y="160" width="180" height="44" rx="6"
                fill="#1F7AFF" fillOpacity="0.85" style={{color: '#1F7AFF'}}/>
          <rect className="section-block" data-id="N2" data-tier="2"
                x="350" y="160" width="180" height="44" rx="6"
                fill="#1F7AFF" fillOpacity="0.85" style={{color: '#1F7AFF'}}/>
          <rect className="section-block" data-id="N3" data-tier="2"
                x="540" y="160" width="120" height="44" rx="6"
                fill="#1F7AFF" fillOpacity="0.85" style={{color: '#1F7AFF'}}/>
          <rect className="section-block" data-id="N4" data-tier="2"
                x="670" y="160" width="180" height="44" rx="6"
                fill="#1F7AFF" fillOpacity="0.85" style={{color: '#1F7AFF'}}/>
          <rect className="section-block" data-id="N5" data-tier="2"
                x="860" y="160" width="180" height="44" rx="6"
                fill="#1F7AFF" fillOpacity="0.85" style={{color: '#1F7AFF'}}/>
          <rect className="section-block" data-id="N6" data-tier="3"
                x="220" y="214" width="170" height="38" rx="6"
                fill="#B89DFF" fillOpacity="0.85" style={{color: '#B89DFF'}}/>
          <rect className="section-block" data-id="N7" data-tier="3"
                x="400" y="214" width="170" height="38" rx="6"
                fill="#B89DFF" fillOpacity="0.85" style={{color: '#B89DFF'}}/>
          <rect className="section-block" data-id="N8" data-tier="3"
                x="580" y="214" width="40" height="38" rx="6"
                fill="#B89DFF" fillOpacity="0.85" style={{color: '#B89DFF'}}/>
          <rect className="section-block" data-id="N9" data-tier="3"
                x="630" y="214" width="170" height="38" rx="6"
                fill="#B89DFF" fillOpacity="0.85" style={{color: '#B89DFF'}}/>
          <rect className="section-block" data-id="N10" data-tier="3"
                x="810" y="214" width="170" height="38" rx="6"
                fill="#B89DFF" fillOpacity="0.85" style={{color: '#B89DFF'}}/>
          <rect className="section-block" data-id="VIP-N1" data-tier="4"
                x="290" y="252" width="100" height="14" rx="3"
                fill="#FFD27A" fillOpacity="0.95" style={{color: '#FFD27A'}}/>
          <rect className="section-block" data-id="VIP-N2" data-tier="4"
                x="395" y="252" width="100" height="14" rx="3"
                fill="#FFD27A" fillOpacity="0.95" style={{color: '#FFD27A'}}/>
          <rect className="section-block" data-id="VIP-N3" data-tier="4"
                x="500" y="252" width="100" height="14" rx="3"
                fill="#FFD27A" fillOpacity="0.95" style={{color: '#FFD27A'}}/>
          <rect className="section-block" data-id="VIP-N4" data-tier="4"
                x="605" y="252" width="100" height="14" rx="3"
                fill="#FFD27A" fillOpacity="0.95" style={{color: '#FFD27A'}}/>
          <rect className="section-block" data-id="VIP-N5" data-tier="4"
                x="710" y="252" width="100" height="14" rx="3"
                fill="#FFD27A" fillOpacity="0.95" style={{color: '#FFD27A'}}/>
          <rect className="section-block" data-id="VIP-N6" data-tier="4"
                x="815" y="252" width="100" height="14" rx="3"
                fill="#FFD27A" fillOpacity="0.95" style={{color: '#FFD27A'}}/>
        </g>

        {/* South stand */}
        <g id="standSouth">
          <rect className="section-block" data-id="VIP-S1" data-tier="4"
                x="290" y="534" width="100" height="14" rx="3"
                fill="#FFD27A" fillOpacity="0.95" style={{color: '#FFD27A'}}/>
          <rect className="section-block" data-id="VIP-S2" data-tier="4"
                x="395" y="534" width="100" height="14" rx="3"
                fill="#FFD27A" fillOpacity="0.95" style={{color: '#FFD27A'}}/>
          <rect className="section-block" data-id="VIP-S3" data-tier="4"
                x="500" y="534" width="100" height="14" rx="3"
                fill="#FFD27A" fillOpacity="0.95" style={{color: '#FFD27A'}}/>
          <rect className="section-block" data-id="VIP-S4" data-tier="4"
                x="605" y="534" width="100" height="14" rx="3"
                fill="#FFD27A" fillOpacity="0.95" style={{color: '#FFD27A'}}/>
          <rect className="section-block" data-id="VIP-S5" data-tier="4"
                x="710" y="534" width="100" height="14" rx="3"
                fill="#FFD27A" fillOpacity="0.95" style={{color: '#FFD27A'}}/>
          <rect className="section-block" data-id="VIP-S6" data-tier="4"
                x="815" y="534" width="100" height="14" rx="3"
                fill="#FFD27A" fillOpacity="0.95" style={{color: '#FFD27A'}}/>
          <rect className="section-block" data-id="S6" data-tier="3"
                x="220" y="548" width="170" height="38" rx="6"
                fill="#B89DFF" fillOpacity="0.85" style={{color: '#B89DFF'}}/>
          <rect className="section-block" data-id="S7" data-tier="3"
                x="400" y="548" width="170" height="38" rx="6"
                fill="#B89DFF" fillOpacity="0.85" style={{color: '#B89DFF'}}/>
          <rect className="section-block" data-id="S8" data-tier="3"
                x="580" y="548" width="40" height="38" rx="6"
                fill="#B89DFF" fillOpacity="0.85" style={{color: '#B89DFF'}}/>
          <rect className="section-block" data-id="S9" data-tier="3"
                x="630" y="548" width="170" height="38" rx="6"
                fill="#B89DFF" fillOpacity="0.85" style={{color: '#B89DFF'}}/>
          <rect className="section-block" data-id="S10" data-tier="3"
                x="810" y="548" width="170" height="38" rx="6"
                fill="#B89DFF" fillOpacity="0.85" style={{color: '#B89DFF'}}/>
          <rect className="section-block" data-id="S1" data-tier="2"
                x="160" y="596" width="180" height="44" rx="6"
                fill="#1F7AFF" fillOpacity="0.85" style={{color: '#1F7AFF'}}/>
          <rect className="section-block" data-id="S2" data-tier="2"
                x="350" y="596" width="180" height="44" rx="6"
                fill="#1F7AFF" fillOpacity="0.85" style={{color: '#1F7AFF'}}/>
          <rect className="section-block" data-id="S3" data-tier="2"
                x="540" y="596" width="120" height="44" rx="6"
                fill="#1F7AFF" fillOpacity="0.85" style={{color: '#1F7AFF'}}/>
          <rect className="section-block" data-id="S4" data-tier="2"
                x="670" y="596" width="180" height="44" rx="6"
                fill="#1F7AFF" fillOpacity="0.85" style={{color: '#1F7AFF'}}/>
          <rect className="section-block" data-id="S5" data-tier="2"
                x="860" y="596" width="180" height="44" rx="6"
                fill="#1F7AFF" fillOpacity="0.85" style={{color: '#1F7AFF'}}/>
        </g>

        {/* East/West (Bronze - corners) */}
        <g id="standEast">
          <rect className="section-block" data-id="E1" data-tier="1"
                x="980" y="220" width="80" height="60" rx="8"
                fill="#5BD0FF" fillOpacity="0.85" style={{color: '#5BD0FF'}}/>
          <rect className="section-block" data-id="E2" data-tier="1"
                x="980" y="290" width="80" height="100" rx="8"
                fill="#5BD0FF" fillOpacity="0.85" style={{color: '#5BD0FF'}}/>
          <rect className="section-block" data-id="E3" data-tier="1"
                x="980" y="400" width="80" height="100" rx="8"
                fill="#5BD0FF" fillOpacity="0.85" style={{color: '#5BD0FF'}}/>
          <rect className="section-block" data-id="E4" data-tier="1"
                x="980" y="510" width="80" height="60" rx="8"
                fill="#5BD0FF" fillOpacity="0.85" style={{color: '#5BD0FF'}}/>
        </g>
        <g id="standWest">
          <rect className="section-block" data-id="W1" data-tier="1"
                x="140" y="220" width="80" height="60" rx="8"
                fill="#5BD0FF" fillOpacity="0.85" style={{color: '#5BD0FF'}}/>
          <rect className="section-block" data-id="W2" data-tier="1"
                x="140" y="290" width="80" height="100" rx="8"
                fill="#5BD0FF" fillOpacity="0.85" style={{color: '#5BD0FF'}}/>
          <rect className="section-block" data-id="W3" data-tier="1"
                x="140" y="400" width="80" height="100" rx="8"
                fill="#5BD0FF" fillOpacity="0.85" style={{color: '#5BD0FF'}}/>
          <rect className="section-block" data-id="W4" data-tier="1"
                x="140" y="510" width="80" height="60" rx="8"
                fill="#5BD0FF" fillOpacity="0.85" style={{color: '#5BD0FF'}}/>
        </g>
      </svg>

      <div className="section-tooltip" id="sectionTooltip" ref={refs.sectionTooltipRef}>
        <div className="tooltip-name" id="ttName">VIP NORTH 3</div>
        <div className="tooltip-info">
          <span className="swatch-mini"></span>
          <span className="tier-label">VIP</span>
          <span>· €150</span>
        </div>
        <div className="tooltip-avail" id="ttAvail">42 seats available</div>
      </div>
    </div>

    <div className="section-detail" id="sectionDetail" ref={refs.sectionDetailRef}>
      <div className="sd-tier-bar">
        <span className="swatch"></span>
        <span>VIP TIER</span>
      </div>
      <div className="sd-name">VIP North Stand 3</div>
      <div className="sd-sub">Premium center-line seats with the best view of the pitch and complimentary lounge access.</div>
      <div className="sd-row">
        <span className="lbl">Available</span>
        <span className="val">42 seats</span>
      </div>
      <div className="sd-row">
        <span className="lbl">Row range</span>
        <span className="val">A – F</span>
      </div>
      <div className="sd-row">
        <span className="lbl">Includes</span>
        <span className="val">Lounge access</span>
      </div>
      <div className="sd-price">
        <span className="from">From</span>
        <span className="amount">€150</span>
      </div>
      <button className="sd-cta" id="sdCta" ref={refs.sdCtaRef}>
        <span>Seat selection</span>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M3 7L11 7M7 3L11 7L7 11" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </div>
  </div>

  {/* ========= SEAT ROW OVERLAY (2 rows x 10 chairs) ========= */}
  <div className="seats-overlay">
    <div className="seats-grid" id="seatsGrid">
      {/* Front row (10 seats) - middle one (5th) is the highlighted/center seat */}
      <div className="seat-chair"></div>
      <div className="seat-chair"></div>
      <div className="seat-chair"></div>
      <div className="seat-chair"></div>
      <div className="seat-chair is-center">
        <svg className="check" viewBox="0 0 14 14" fill="none">
          <path d="M3 7L6 10L11 4" stroke="currentColor" strokeWidth="2.4"
                strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      <div className="seat-chair"></div>
      <div className="seat-chair"></div>
      <div className="seat-chair"></div>
      <div className="seat-chair"></div>
      <div className="seat-chair"></div>
      {/* Back row */}
      <div className="seat-chair"></div>
      <div className="seat-chair"></div>
      <div className="seat-chair"></div>
      <div className="seat-chair"></div>
      <div className="seat-chair"></div>
      <div className="seat-chair"></div>
      <div className="seat-chair"></div>
      <div className="seat-chair"></div>
      <div className="seat-chair"></div>
      <div className="seat-chair"></div>
    </div>
  </div>

  {/* ========= SEAT DETAIL MODAL ========= */}
  <div className="modal-wrap">
    <div className="modal" id="modal">
      <div className="meta-row">
        <div className="meta-cell">
          <span className="meta-label">Section</span>
          <span className="meta-value">VIP-N3</span>
        </div>
        <div className="meta-divider"></div>
        <div className="meta-cell">
          <span className="meta-label">Row</span>
          <span className="meta-value">12</span>
        </div>
        <div className="meta-divider"></div>
        <div className="meta-cell">
          <span className="meta-label">Seat</span>
          <span className="meta-value">23</span>
        </div>
      </div>
      <div className="pricebar" id="pricebar">
        <div className="pricebar-left">
          <div className="check-circle">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M3 7L6 10L11 4" stroke="#0A1628" strokeWidth="2.4"
                    strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="pricebar-text">
            <span className="pricebar-title" id="pricebarTitle" ref={refs.pricebarTitleRef}>VIP North 3</span>
            <span className="pricebar-sub" id="pricebarSub" ref={refs.pricebarSubRef}>Premium · Lounge access included</span>
          </div>
        </div>
        <div className="pricebar-right">
          <span className="price-old">€175</span>
          <span className="price-new">€150</span>
        </div>
      </div>
      <div className="actions">
        <button className="btn btn-secondary" id="btnClose" ref={refs.btnCloseRef}>Close</button>
        <button className="btn btn-primary" id="btnPrimary" ref={refs.btnPrimaryRef}>
          <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
            <path d="M3.5 8L6.5 11L12.5 5" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span id="btnPrimaryLabel" ref={refs.btnPrimaryLabelRef}>Select seat</span>
        </button>
      </div>
    </div>
  </div>

  {/* Step 3: QR Ticket card */}
  <div className="ticket-card" id="ticketCard" ref={refs.ticketCardRef}>
    <div className="ticket-info">
      {/* Decorative stadium/championship background */}
      <img className="ticket-bg-art" src="/images/stadium-ticket-bg.jpg" alt="" aria-hidden="true" />

      {/* Readability scrim behind text */}
      <div className="ticket-text-scrim"></div>

      <div className="ticket-info-content">
        <div className="ticket-header">
          <span className="club-logo club-logo-ajax" aria-label="Ajax">
            <img src="/images/ajax-logo.png" alt="Ajax" />
          </span>
          <div className="ticket-header-center">
            <div className="ticket-event">
              <div className="ticket-event-line">Eredivisie</div>
              <div className="ticket-event-date">02 May 2026</div>
            </div>
            <div className="ticket-matchup">
              <span className="club-name">Ajax</span>
              <span className="vs-dash">—</span>
              <span className="club-name">Feyenoord</span>
            </div>
          </div>
          <span className="club-logo club-logo-fey" aria-label="Feyenoord">
            <img src="/images/feyenoord-logo.png" alt="Feyenoord" />
          </span>
        </div>
        <div className="ticket-meta-grid">
          <div className="ticket-meta-cell">
            <span className="ticket-meta-label">Section</span>
            <span className="ticket-meta-value">VIP-N3</span>
          </div>
          <div className="ticket-meta-cell">
            <span className="ticket-meta-label">Gate</span>
            <span className="ticket-meta-value">G · North</span>
          </div>
          <div className="ticket-meta-cell">
            <span className="ticket-meta-label">Row</span>
            <span className="ticket-meta-value">12</span>
          </div>
          <div className="ticket-meta-cell">
            <span className="ticket-meta-label">Seat</span>
            <span className="ticket-meta-value">23</span>
          </div>
        </div>
      </div>
    </div>

    <div className="ticket-perforation"></div>

    <div className="qr-section">
      <div className="qr-card" id="qrCard" ref={refs.qrCardRef}>
        <svg viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg" shapeRendering="crispEdges">
          <rect width="21" height="21" fill="white"/>
          <g fill="black">
            {/* Top-left finder */}
            <rect x="0" y="0" width="7" height="7"/>
            <rect x="1" y="1" width="5" height="5" fill="white"/>
            <rect x="2" y="2" width="3" height="3"/>
            {/* Top-right finder */}
            <rect x="14" y="0" width="7" height="7"/>
            <rect x="15" y="1" width="5" height="5" fill="white"/>
            <rect x="16" y="2" width="3" height="3"/>
            {/* Bottom-left finder */}
            <rect x="0" y="14" width="7" height="7"/>
            <rect x="1" y="15" width="5" height="5" fill="white"/>
            <rect x="2" y="16" width="3" height="3"/>
            {/* Timing patterns + data modules (pseudo-random) */}
            <rect x="8" y="0" width="1" height="1"/><rect x="10" y="0" width="1" height="1"/>
            <rect x="11" y="0" width="1" height="1"/><rect x="13" y="0" width="1" height="1"/>
            <rect x="9" y="1" width="1" height="1"/><rect x="12" y="1" width="1" height="1"/>
            <rect x="8" y="2" width="1" height="1"/><rect x="11" y="2" width="1" height="1"/>
            <rect x="13" y="2" width="1" height="1"/>
            <rect x="9" y="3" width="1" height="1"/><rect x="10" y="3" width="1" height="1"/>
            <rect x="12" y="3" width="1" height="1"/>
            <rect x="8" y="4" width="1" height="1"/><rect x="11" y="4" width="1" height="1"/>
            <rect x="13" y="4" width="1" height="1"/>
            <rect x="9" y="5" width="1" height="1"/><rect x="10" y="5" width="1" height="1"/>
            <rect x="12" y="5" width="1" height="1"/>
            <rect x="0" y="8" width="1" height="1"/><rect x="2" y="8" width="1" height="1"/>
            <rect x="3" y="8" width="1" height="1"/><rect x="5" y="8" width="1" height="1"/>
            <rect x="7" y="8" width="1" height="1"/><rect x="9" y="8" width="1" height="1"/>
            <rect x="10" y="8" width="1" height="1"/><rect x="12" y="8" width="1" height="1"/>
            <rect x="14" y="8" width="1" height="1"/><rect x="16" y="8" width="1" height="1"/>
            <rect x="17" y="8" width="1" height="1"/><rect x="19" y="8" width="1" height="1"/>
            <rect x="1" y="9" width="1" height="1"/><rect x="4" y="9" width="1" height="1"/>
            <rect x="6" y="9" width="1" height="1"/><rect x="8" y="9" width="1" height="1"/>
            <rect x="11" y="9" width="1" height="1"/><rect x="13" y="9" width="1" height="1"/>
            <rect x="15" y="9" width="1" height="1"/><rect x="18" y="9" width="1" height="1"/>
            <rect x="20" y="9" width="1" height="1"/>
            <rect x="0" y="10" width="1" height="1"/><rect x="3" y="10" width="1" height="1"/>
            <rect x="5" y="10" width="1" height="1"/><rect x="7" y="10" width="1" height="1"/>
            <rect x="9" y="10" width="1" height="1"/><rect x="12" y="10" width="1" height="1"/>
            <rect x="14" y="10" width="1" height="1"/><rect x="17" y="10" width="1" height="1"/>
            <rect x="19" y="10" width="1" height="1"/>
            <rect x="2" y="11" width="1" height="1"/><rect x="4" y="11" width="1" height="1"/>
            <rect x="6" y="11" width="1" height="1"/><rect x="10" y="11" width="1" height="1"/>
            <rect x="11" y="11" width="1" height="1"/><rect x="13" y="11" width="1" height="1"/>
            <rect x="15" y="11" width="1" height="1"/><rect x="18" y="11" width="1" height="1"/>
            <rect x="20" y="11" width="1" height="1"/>
            <rect x="1" y="12" width="1" height="1"/><rect x="3" y="12" width="1" height="1"/>
            <rect x="8" y="12" width="1" height="1"/><rect x="11" y="12" width="1" height="1"/>
            <rect x="14" y="12" width="1" height="1"/><rect x="16" y="12" width="1" height="1"/>
            <rect x="19" y="12" width="1" height="1"/>
            <rect x="0" y="13" width="1" height="1"/><rect x="5" y="13" width="1" height="1"/>
            <rect x="7" y="13" width="1" height="1"/><rect x="9" y="13" width="1" height="1"/>
            <rect x="12" y="13" width="1" height="1"/><rect x="13" y="13" width="1" height="1"/>
            <rect x="15" y="13" width="1" height="1"/><rect x="17" y="13" width="1" height="1"/>
            <rect x="20" y="13" width="1" height="1"/>
            <rect x="8" y="14" width="1" height="1"/><rect x="10" y="14" width="1" height="1"/>
            <rect x="11" y="14" width="1" height="1"/><rect x="13" y="14" width="1" height="1"/>
            <rect x="15" y="14" width="1" height="1"/><rect x="18" y="14" width="1" height="1"/>
            <rect x="9" y="15" width="1" height="1"/><rect x="12" y="15" width="1" height="1"/>
            <rect x="14" y="15" width="1" height="1"/><rect x="16" y="15" width="1" height="1"/>
            <rect x="17" y="15" width="1" height="1"/><rect x="20" y="15" width="1" height="1"/>
            <rect x="8" y="16" width="1" height="1"/><rect x="11" y="16" width="1" height="1"/>
            <rect x="13" y="16" width="1" height="1"/><rect x="15" y="16" width="1" height="1"/>
            <rect x="19" y="16" width="1" height="1"/>
            <rect x="9" y="17" width="1" height="1"/><rect x="10" y="17" width="1" height="1"/>
            <rect x="12" y="17" width="1" height="1"/><rect x="14" y="17" width="1" height="1"/>
            <rect x="17" y="17" width="1" height="1"/><rect x="18" y="17" width="1" height="1"/>
            <rect x="20" y="17" width="1" height="1"/>
            <rect x="8" y="18" width="1" height="1"/><rect x="11" y="18" width="1" height="1"/>
            <rect x="13" y="18" width="1" height="1"/><rect x="16" y="18" width="1" height="1"/>
            <rect x="19" y="18" width="1" height="1"/>
            <rect x="9" y="19" width="1" height="1"/><rect x="10" y="19" width="1" height="1"/>
            <rect x="12" y="19" width="1" height="1"/><rect x="15" y="19" width="1" height="1"/>
            <rect x="17" y="19" width="1" height="1"/><rect x="20" y="19" width="1" height="1"/>
            <rect x="8" y="20" width="1" height="1"/><rect x="11" y="20" width="1" height="1"/>
            <rect x="14" y="20" width="1" height="1"/><rect x="16" y="20" width="1" height="1"/>
            <rect x="18" y="20" width="1" height="1"/>
          </g>
        </svg>
        <div className="qr-checkmark">
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M5 13l4 4L19 7" stroke="white" strokeWidth="3.5"
                  strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>
      <div className="qr-caption">Scan at gate</div>
    </div>

    <div className="ticket-actions">
      <button className="ticket-btn" id="ticketBtnWallet" ref={refs.ticketBtnWalletRef}>
        <svg viewBox="0 0 24 24" fill="none">
          <rect x="3" y="6" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.8"/>
          <path d="M3 10h18" stroke="currentColor" strokeWidth="1.8"/>
        </svg>
        Add to Wallet
      </button>
      <button className="ticket-btn ticket-btn-primary" id="ticketBtnSave" ref={refs.ticketBtnSaveRef}>
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M12 3v13m0 0l-5-5m5 5l5-5M5 21h14"
                stroke="currentColor" strokeWidth="2.2"
                strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Save ticket
      </button>
    </div>
  </div>

  {/* Optional services row under ticket */}
  <div className="ticket-services" id="ticketServices">
    <div className="service-pill">
      <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M2 6l6-3 6 3v4l-6 3-6-3V6z" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M8 9v4" stroke="currentColor" strokeWidth="1.4"/>
      </svg>
      <span>Sell on Marketplace</span>
    </div>
    <div className="service-pill">
      <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <rect x="2" y="3" width="12" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M2 6h12" stroke="currentColor" strokeWidth="1.4"/>
      </svg>
      <span>Season cards</span>
    </div>
    <div className="service-pill">
      <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <rect x="3" y="2" width="10" height="12" rx="2" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M7 12h2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
      <span>Club Application</span>
    </div>
    <div className="service-pill">
      <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M3 5l5-2 5 2v3c0 3-2.2 5.4-5 6-2.8-.6-5-3-5-6V5z" stroke="currentColor" strokeWidth="1.4"/>
      </svg>
      <span>Merchandise</span>
    </div>
    <div className="service-pill">
      <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M5 8c0-1.5 1.5-3 3-3s3 1.5 3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        <path d="M3 8c0-2.5 2.2-5 5-5s5 2.5 5 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" opacity="0.5"/>
        <circle cx="8" cy="11" r="1.2" fill="currentColor"/>
      </svg>
      <span>Tap 2 Pay</span>
    </div>
  </div>

  {/* Step 4: Gate scanner */}
  <div className="gate-scanner" id="gateScanner" ref={refs.gateScannerRef}>
    <div className="scanner-frame">
      <div className="scanner-label">Gate G · Live scanner</div>
      <div className="scanner-screen">
        {/* Mini QR being scanned (mirror of the ticket QR) */}
        <div className="scanned-qr" id="scannedQr">
          <svg viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg" shapeRendering="crispEdges">
            <rect width="21" height="21" fill="white"/>
            <g fill="black">
              <rect x="0" y="0" width="7" height="7"/>
              <rect x="1" y="1" width="5" height="5" fill="white"/>
              <rect x="2" y="2" width="3" height="3"/>
              <rect x="14" y="0" width="7" height="7"/>
              <rect x="15" y="1" width="5" height="5" fill="white"/>
              <rect x="16" y="2" width="3" height="3"/>
              <rect x="0" y="14" width="7" height="7"/>
              <rect x="1" y="15" width="5" height="5" fill="white"/>
              <rect x="2" y="16" width="3" height="3"/>
              <rect x="8" y="0" width="1" height="1"/><rect x="10" y="0" width="1" height="1"/>
              <rect x="11" y="0" width="1" height="1"/><rect x="13" y="0" width="1" height="1"/>
              <rect x="9" y="1" width="1" height="1"/><rect x="12" y="1" width="1" height="1"/>
              <rect x="8" y="2" width="1" height="1"/><rect x="11" y="2" width="1" height="1"/>
              <rect x="13" y="2" width="1" height="1"/>
              <rect x="9" y="3" width="1" height="1"/><rect x="10" y="3" width="1" height="1"/>
              <rect x="12" y="3" width="1" height="1"/>
              <rect x="8" y="4" width="1" height="1"/><rect x="11" y="4" width="1" height="1"/>
              <rect x="13" y="4" width="1" height="1"/>
              <rect x="9" y="5" width="1" height="1"/><rect x="10" y="5" width="1" height="1"/>
              <rect x="12" y="5" width="1" height="1"/>
              <rect x="0" y="8" width="1" height="1"/><rect x="2" y="8" width="1" height="1"/>
              <rect x="3" y="8" width="1" height="1"/><rect x="5" y="8" width="1" height="1"/>
              <rect x="7" y="8" width="1" height="1"/><rect x="9" y="8" width="1" height="1"/>
              <rect x="10" y="8" width="1" height="1"/><rect x="12" y="8" width="1" height="1"/>
              <rect x="14" y="8" width="1" height="1"/><rect x="16" y="8" width="1" height="1"/>
              <rect x="17" y="8" width="1" height="1"/><rect x="19" y="8" width="1" height="1"/>
              <rect x="1" y="9" width="1" height="1"/><rect x="4" y="9" width="1" height="1"/>
              <rect x="6" y="9" width="1" height="1"/><rect x="8" y="9" width="1" height="1"/>
              <rect x="11" y="9" width="1" height="1"/><rect x="13" y="9" width="1" height="1"/>
              <rect x="15" y="9" width="1" height="1"/><rect x="18" y="9" width="1" height="1"/>
              <rect x="20" y="9" width="1" height="1"/>
              <rect x="0" y="10" width="1" height="1"/><rect x="3" y="10" width="1" height="1"/>
              <rect x="5" y="10" width="1" height="1"/><rect x="7" y="10" width="1" height="1"/>
              <rect x="9" y="10" width="1" height="1"/><rect x="12" y="10" width="1" height="1"/>
              <rect x="14" y="10" width="1" height="1"/><rect x="17" y="10" width="1" height="1"/>
              <rect x="19" y="10" width="1" height="1"/>
              <rect x="2" y="11" width="1" height="1"/><rect x="4" y="11" width="1" height="1"/>
              <rect x="6" y="11" width="1" height="1"/><rect x="10" y="11" width="1" height="1"/>
              <rect x="11" y="11" width="1" height="1"/><rect x="13" y="11" width="1" height="1"/>
              <rect x="15" y="11" width="1" height="1"/><rect x="18" y="11" width="1" height="1"/>
              <rect x="20" y="11" width="1" height="1"/>
              <rect x="1" y="12" width="1" height="1"/><rect x="3" y="12" width="1" height="1"/>
              <rect x="8" y="12" width="1" height="1"/><rect x="11" y="12" width="1" height="1"/>
              <rect x="14" y="12" width="1" height="1"/><rect x="16" y="12" width="1" height="1"/>
              <rect x="19" y="12" width="1" height="1"/>
              <rect x="9" y="13" width="1" height="1"/><rect x="12" y="13" width="1" height="1"/>
              <rect x="15" y="13" width="1" height="1"/><rect x="17" y="13" width="1" height="1"/>
              <rect x="20" y="13" width="1" height="1"/>
              <rect x="8" y="15" width="1" height="1"/><rect x="11" y="15" width="1" height="1"/>
              <rect x="13" y="15" width="1" height="1"/><rect x="16" y="15" width="1" height="1"/>
              <rect x="18" y="15" width="1" height="1"/>
              <rect x="9" y="16" width="1" height="1"/><rect x="12" y="16" width="1" height="1"/>
              <rect x="15" y="16" width="1" height="1"/><rect x="20" y="16" width="1" height="1"/>
              <rect x="8" y="17" width="1" height="1"/><rect x="10" y="17" width="1" height="1"/>
              <rect x="14" y="17" width="1" height="1"/><rect x="17" y="17" width="1" height="1"/>
              <rect x="19" y="17" width="1" height="1"/>
              <rect x="9" y="18" width="1" height="1"/><rect x="11" y="18" width="1" height="1"/>
              <rect x="13" y="18" width="1" height="1"/><rect x="16" y="18" width="1" height="1"/>
              <rect x="19" y="18" width="1" height="1"/>
              <rect x="9" y="19" width="1" height="1"/><rect x="10" y="19" width="1" height="1"/>
              <rect x="12" y="19" width="1" height="1"/><rect x="15" y="19" width="1" height="1"/>
              <rect x="17" y="19" width="1" height="1"/><rect x="20" y="19" width="1" height="1"/>
              <rect x="8" y="20" width="1" height="1"/><rect x="11" y="20" width="1" height="1"/>
              <rect x="14" y="20" width="1" height="1"/><rect x="16" y="20" width="1" height="1"/>
              <rect x="18" y="20" width="1" height="1"/>
            </g>
          </svg>
        </div>
        <div className="scanner-corners"><span></span></div>
        <div className="scanner-laser"></div>
        <div className="scan-success-flash"></div>
        <div className="scan-check">
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="3"
                  strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>
      <div className="welcome-bubble">
        <div className="welcome-title">Welcome — VIP-N3</div>
        <div className="welcome-sub">Row 12 · Seat 23 · Have a great match</div>
      </div>
    </div>
  </div>

  {/* Animated cursor */}
  <div className="cursor" id="cursor" ref={refs.cursorRef}>
    <svg viewBox="0 0 28 28" fill="none">
      <circle cx="14" cy="14" r="12" fill="rgba(255,255,255,0.15)"
              stroke="rgba(255,255,255,0.5)" strokeWidth="1"/>
      <circle cx="14" cy="14" r="5" fill="white"
              stroke="rgba(0,0,0,0.2)" strokeWidth="0.5"/>
      <circle cx="14" cy="14" r="2.5" fill="var(--accent-cyan)"/>
    </svg>
    <div className="ripple"></div>
  </div>
  {/* Hidden timer for animation hook */}
  <span className="hidden-timer" ref={refs.timerRef} aria-hidden="true" style={{position:'absolute',left:'-9999px',top:'-9999px'}}>0.0s</span>
</div>

    </div>{/* /.showcase-box */}
  </div>{/* /.showcase-frame */}

</section>
  );
}

export default SeatedShowcase;
